import { fetchDashboardData } from './dashboardService';
import { auth } from '../firebase';
import { adjustPrayerTimes } from '../utils/prayerTimeUtils';
import { storage } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { isIOS, isAndroid, waitForAndroidInterface, getPlatform } from '../utils/platformDetection';
import { fetchPrayerTimes } from './prayerService';

/**
 * Cross-platform Native Bridge
 * Handles communication with native apps on iOS and Android
 */
class NativeBridge {
    constructor() {
        console.log('[DEBUG] Initializing NativeBridge...');
        
        // Initialize state
        this.lastDataSentDate = null;
        this.settings = null;
        this.platform = getPlatform();
        this.androidInterface = null;
        
        console.log(`[DEBUG] Detected platform: ${this.platform}`);
        
        // Initialize Android interface if needed
        if (this.platform === 'android') {
            this.initAndroidInterface();
        }
        
        // Create the global bridge interface - same API across all platforms
        this.initializeGlobalBridge();
        
        // Add debug utilities
        this.initializeDebugUtilities();
    }
    
    /**
     * Initialize Android interface with timeout
     */
    async initAndroidInterface() {
        console.log('[DEBUG] Initializing Android interface...');
        try {
            this.androidInterface = await waitForAndroidInterface();
            console.log('[DEBUG] Android interface initialized:', !!this.androidInterface);
        } catch (error) {
            console.error('[ERROR] Failed to initialize Android interface:', error);
            this.androidInterface = null;
        }
    }
    
    /**
     * Initialize the global bridge object that's the same across all platforms
     */
    initializeGlobalBridge() {
        // Create the global bridge object with a consistent API
        window.ummahtiIosBridge = {
            getPrayerTimesAndLocation: async () => {
                try {
                    console.log('[DEBUG] Starting getPrayerTimesAndLocation...');
                    console.log('[DEBUG] Current bridge state:', {
                        platform: this.platform,
                        hasSettings: !!this.settings,
                        settingsSnapshot: this.settings,
                        user: auth.currentUser?.uid
                    });
                    
                    // Declare user and settingsToUse at the top
                    const user = auth.currentUser;
                    let settingsToUse = this.settings;
                    let isGuest = false;
                    let bridgeData;
                    
                    if (!user) {
                        // Guest user: load guest settings from localStorage
                        const GUEST_SETTINGS_KEY = 'ummahti_guest_settings';
                        let guestSettings = {};
                        try {
                            const data = localStorage.getItem(GUEST_SETTINGS_KEY);
                            guestSettings = data ? JSON.parse(data) : {};
                        } catch (e) {
                            guestSettings = {};
                        }
                        // Fallbacks for missing fields
                        guestSettings.calculationMethod = guestSettings.calculationMethod || 3;
                        guestSettings.timezone = guestSettings.timezone || 'Europe/Amsterdam';
                        guestSettings.tunedTimes = guestSettings.tunedTimes || {};
                        guestSettings.location = guestSettings.location || '';
                        guestSettings.coordinates = guestSettings.coordinates || null;

                        // Only add fields from this.settings if not present in guestSettings
                        const mergedSettings = { ...this.settings, ...guestSettings };
                        // Ensure guestSettings fields take precedence
                        mergedSettings.calculationMethod = guestSettings.calculationMethod;
                        mergedSettings.tunedTimes = guestSettings.tunedTimes;
                        mergedSettings.timezone = guestSettings.timezone;
                        mergedSettings.location = guestSettings.location;
                        mergedSettings.coordinates = guestSettings.coordinates;

                        settingsToUse = mergedSettings;
                        isGuest = true;
                        // If no valid location or coordinates, return error response instead of throwing
                        if (!settingsToUse?.location || !settingsToUse?.coordinates) {
                            console.warn('[WARNING] Settings missing location or coordinates. Returning error response.');
                            return {
                                error: "Location not set",
                                prayerTimes: {},
                                tomorrowPrayerTimes: {},
                                weekPrayerTimes: [],
                                location: {
                                    latitude: 0,
                                    longitude: 0,
                                    cityName: ''
                                },
                                timestamp: new Date().toISOString(),
                                isGuest
                            };
                        }
                        // --- For guests, fetch prayer times directly ---
                        const prayerTimesData = await fetchPrayerTimes(settingsToUse);
                        const tomorrow = new Date();
                        tomorrow.setDate(tomorrow.getDate() + 1);
                        const tomorrowPrayerTimesData = await fetchPrayerTimes(settingsToUse, tomorrow);
                        // Fetch a full week of prayer times, starting from tomorrow
                        console.log('[DEBUG] Fetching week prayer times for guest...');
                        const weekPrayerTimesArray = await this.fetchWeekPrayerTimes(settingsToUse);
                        // Format data in a consistent format for all platforms
                        bridgeData = {
                            prayerTimes: {
                                fajr: prayerTimesData.times.Fajr,
                                dhuhr: prayerTimesData.times.Dhuhr,
                                asr: prayerTimesData.times.Asr,
                                maghrib: prayerTimesData.times.Maghrib,
                                isha: prayerTimesData.times.Isha
                            },
                            tomorrowPrayerTimes: tomorrowPrayerTimesData ? {
                                fajr: tomorrowPrayerTimesData.times.Fajr,
                                dhuhr: tomorrowPrayerTimesData.times.Dhuhr,
                                asr: tomorrowPrayerTimesData.times.Asr,
                                maghrib: tomorrowPrayerTimesData.times.Maghrib,
                                isha: tomorrowPrayerTimesData.times.Isha
                            } : null,
                            weekPrayerTimes: weekPrayerTimesArray.length > 0 ? weekPrayerTimesArray : null,
                            location: {
                                latitude: Number(settingsToUse.coordinates?.lat || settingsToUse.coordinates?.latitude) || 0,
                                longitude: Number(settingsToUse.coordinates?.lon || settingsToUse.coordinates?.longitude) || 0,
                                cityName: settingsToUse.location || ''
                            },
                            timestamp: new Date().toISOString(),
                            tunedTimes: settingsToUse.tunedTimes || {},
                            isGuest,
                            loginStateChanged: this.lastLoginState // Include the login state change flag
                        };
                        // Log the data we're about to send
                        console.log('[DEBUG] Sending guest bridge data for platform:', this.platform);
                        // Clear login state after sending once
                        this.lastLoginState = null;
                        // Update last sent date
                        this.lastDataSentDate = new Date().toDateString();
                        // Send to the appropriate platform
                        this.sendToPlatform('prayerTimes', bridgeData);
                        return bridgeData; // <--- Ensure we return here for guests!
                    }
                    // --- Only logged-in user logic below ---
                    // Authenticated user flow
                    // Validate settings
                    if (!settingsToUse) {
                        console.error('[ERROR] Settings not initialized. Current settings:', settingsToUse);
                        throw new Error('Settings not initialized');
                    }
                    // Wait for dashboard data with proper parameters
                    console.log('[DEBUG] Fetching dashboard data with:', {
                        userId: user.uid,
                        hasSettings: !!settingsToUse,
                        settingsKeys: settingsToUse ? Object.keys(settingsToUse) : []
                    });
                    // For logged-in users, use fetchDashboardData
                    const dashboardData = await fetchDashboardData(user, settingsToUse);
                    console.log('[DEBUG] Got dashboard data:', {
                        hasPrayerTimes: !!dashboardData?.prayerTimes,
                        hasTomorrowPrayerTimes: !!dashboardData?.tomorrowPrayerTimes,
                        hasCoordinates: !!dashboardData?.coordinates,
                        hasLocation: !!dashboardData?.location
                    });
                    // Validate data
                    if (!dashboardData || !dashboardData.prayerTimes || !dashboardData.prayerTimes.times) {
                        console.error('[ERROR] Invalid dashboard data:', {
                            dashboardData,
                            user: user.uid,
                            settings: settingsToUse
                        });
                        throw new Error('Invalid dashboard data received');
                    }
                    // If times are already adjusted, use them directly
                    const prayerTimesToUse = dashboardData.prayerTimes.isAdjusted 
                        ? dashboardData.prayerTimes.times
                        : adjustPrayerTimes(dashboardData.prayerTimes.times, settingsToUse.tunedTimes);
                    // Same for tomorrow's times
                    const tomorrowPrayerTimesToUse = dashboardData.tomorrowPrayerTimes?.isAdjusted
                        ? dashboardData.tomorrowPrayerTimes.times
                        : dashboardData.tomorrowPrayerTimes
                            ? adjustPrayerTimes(dashboardData.tomorrowPrayerTimes.times, settingsToUse.tunedTimes)
                            : null;
                    // Fetch a full week of prayer times, starting from tomorrow
                    console.log('[DEBUG] Fetching week prayer times...');
                    const weekPrayerTimesArray = await this.fetchWeekPrayerTimes(settingsToUse);
                    console.log('[DEBUG] Fetched', weekPrayerTimesArray.length, 'days of additional prayer times');
                    // Format data in a consistent format for all platforms
                    bridgeData = {
                        prayerTimes: {
                            fajr: prayerTimesToUse.Fajr,
                            dhuhr: prayerTimesToUse.Dhuhr,
                            asr: prayerTimesToUse.Asr,
                            maghrib: prayerTimesToUse.Maghrib,
                            isha: prayerTimesToUse.Isha
                        },
                        tomorrowPrayerTimes: tomorrowPrayerTimesToUse ? {
                            fajr: tomorrowPrayerTimesToUse.Fajr,
                            dhuhr: tomorrowPrayerTimesToUse.Dhuhr,
                            asr: tomorrowPrayerTimesToUse.Asr,
                            maghrib: tomorrowPrayerTimesToUse.Maghrib,
                            isha: tomorrowPrayerTimesToUse.Isha
                        } : null,
                        weekPrayerTimes: weekPrayerTimesArray.length > 0 ? weekPrayerTimesArray : null,
                        location: {
                            latitude: Number(dashboardData.coordinates?.latitude) || 0,
                            longitude: Number(dashboardData.coordinates?.longitude) || 0,
                            cityName: dashboardData.location || ''
                        },
                        timestamp: new Date().toISOString(),
                        tunedTimes: settingsToUse.tunedTimes || {},
                        isGuest: false,
                        loginStateChanged: this.lastLoginState // Include the login state change flag
                    };
                    // Log the data we're about to send
                    console.log('[DEBUG] Sending bridge data for platform:', this.platform);
                    // Clear login state after sending once
                    this.lastLoginState = null;
                    // Update last sent date
                    this.lastDataSentDate = new Date().toDateString();
                    // Send to the appropriate platform
                    this.sendToPlatform('prayerTimes', bridgeData);
                    return bridgeData;
                } catch (error) {
                    console.error('[ERROR] Error in getPrayerTimesAndLocation:', {
                        error,
                        message: error.message,
                        stack: error.stack
                    });
                    throw error;
                }
            },
            
            notifySettingsChanged: async () => {
                console.log('[DEBUG] Settings changed, refreshing prayer times...');
                return window.ummahtiIosBridge.getPrayerTimesAndLocation();
            },
            
            // New method to handle auth state changes
            notifyAuthStateChanged: async (newState) => {
                console.log('[DEBUG] Auth state changed:', newState);
                this.lastLoginState = newState; // Store the state to include in next prayer times update
                
                // Force a refresh of prayer times when state is loggedOut to ensure widgets get updated
                const forceRefresh = newState === 'loggedOut';
                if (forceRefresh) {
                    console.log('[DEBUG] Forcing prayer times refresh due to logout');
                }
                return window.ummahtiIosBridge.getPrayerTimesAndLocation();
            },
            
            // Adhan methods
            downloadAdhan: (fileName, fileURL) => {
                console.log('[DEBUG] Downloading adhan file:', fileName, 'from URL:', fileURL);
                return this.sendToPlatform('adhan', {
                    action: 'download',
                    fileName: fileName,
                    fileURL: fileURL
                });
            },
            
            playAdhan: (fileName, fileURL = null) => {
                console.log('[DEBUG] Playing adhan:', fileName);
                return this.sendToPlatform('adhan', {
                    action: 'play',
                    fileName: fileName,
                    fileURL: fileURL
                });
            },
            
            stopAdhan: () => {
                console.log('[DEBUG] Stopping adhan playback');
                return this.sendToPlatform('adhan', {
                    action: 'stop'
                });
            },
            
            scheduleAdhan: (prayerName, time, fileName) => {
                console.log('[DEBUG] Scheduling adhan for', prayerName, 'at', time);
                return this.sendToPlatform('adhan', {
                    action: 'schedule',
                    prayerName: prayerName,
                    time: time,
                    fileName: fileName
                });
            },
            
            cancelScheduledAdhan: (prayerName) => {
                console.log('[DEBUG] Cancelling scheduled adhan for', prayerName);
                return this.sendToPlatform('adhan', {
                    action: 'cancelSchedule',
                    prayerName: prayerName
                });
            },
            
            cancelAllScheduledAdhans: () => {
                console.log('[DEBUG] Cancelling all scheduled adhans');
                return this.sendToPlatform('adhan', {
                    action: 'cancelAllSchedules'
                });
            },
            
            updateAdhanSettings: (adhanSettings) => {
                console.log('[DEBUG] Updating adhan settings:', adhanSettings);
                
                try {
                    // Validate required parameters
                    if (!adhanSettings.regularAdhanURL || !adhanSettings.fajrAdhanURL) {
                        console.error('[ERROR] Missing required adhan URLs');
                        return false;
                    }
                    
                    // Save to localStorage for persistence
                    localStorage.setItem('adhanSettings', JSON.stringify({
                        regularAdhanURL: adhanSettings.regularAdhanURL,
                        fajrAdhanURL: adhanSettings.fajrAdhanURL,
                        timestamp: Date.now()
                    }));
                    
                    // Extract file names from URLs
                    const regularFileName = adhanSettings.regularAdhanURL.split('/').pop();
                    const fajrFileName = adhanSettings.fajrAdhanURL.split('/').pop();
                    
                    // Send to platform
                    return this.sendToPlatform('adhan', {
                        action: 'updateSettings',
                        regularAdhanURL: adhanSettings.regularAdhanURL,
                        fajrAdhanURL: adhanSettings.fajrAdhanURL,
                        regularFileName: regularFileName,
                        fajrFileName: fajrFileName
                    });
                } catch (error) {
                    console.error('[ERROR] Error updating adhan settings:', error);
                    return false;
                }
            },
            
            // Method to get Adhan URLs from storage paths
            getAdhanURLs: async () => {
                try {
                    console.log('[DEBUG] Getting adhan URLs from settings');
                    
                    // Check if settings are initialized
                    if (!this.settings?.selectedAdhan) {
                        console.log('[DEBUG] No adhan settings available (possibly guest user)');
                        return null;
                    }
                    
                    const { regularPath, fajrPath } = this.settings.selectedAdhan;
                    
                    if (!regularPath || !fajrPath) {
                        console.log('[DEBUG] Missing adhan paths in settings (possibly guest user)');
                        return null;
                    }
                    
                    console.log('[DEBUG] Getting download URLs for adhan files:', {
                        regularPath,
                        fajrPath
                    });
                    
                    // Get download URLs from Firebase Storage
                    const regularAdhanRef = ref(storage, regularPath);
                    const fajrAdhanRef = ref(storage, fajrPath);
                    
                    const [regularAdhanURL, fajrAdhanURL] = await Promise.all([
                        getDownloadURL(regularAdhanRef),
                        getDownloadURL(fajrAdhanRef)
                    ]);
                    
                    console.log('[DEBUG] Got download URLs:', {
                        regularAdhanURL,
                        fajrAdhanURL
                    });
                    
                    return {
                        regularAdhanURL,
                        fajrAdhanURL
                    };
                } catch (error) {
                    console.error('[ERROR] Error getting adhan URLs:', error);
                    return null;
                }
            },
            
            // Quran audio methods
            playQuranAudio: (audioFiles, startIndex = 0, surahName = '', reciterName = '', shouldPlayBismillah = false) => {
                console.log('[DEBUG] Playing Quran audio:', { 
                    audioFilesCount: audioFiles.length, 
                    startIndex, 
                    surahName, 
                    reciterName, 
                    shouldPlayBismillah 
                });
                
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for playback start
                    const handlePlaybackStart = (event) => {
                        if (event && event.detail && event.detail.currentVerseIndex === startIndex) {
                            window.removeEventListener('quranAudio-verseChanged', handlePlaybackStart);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-verseChanged', handlePlaybackStart);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-verseChanged', handlePlaybackStart);
                        reject(new Error('Playback start timeout'));
                    }, 5000);

                    // Send command to native with bismillah flag
                    this.sendToPlatform('quranAudio', {
                        action: 'play',
                        audioFiles,
                        startIndex,
                        surahName,
                        reciterName,
                        shouldPlayBismillah
                    });
                });
            },
            pauseQuranAudio: () => {
                console.log('[DEBUG] Pausing Quran audio');
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for pause
                    const handlePause = () => {
                        window.removeEventListener('quranAudio-paused', handlePause);
                        resolve();
                    };
                    window.addEventListener('quranAudio-paused', handlePause);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-paused', handlePause);
                        reject(new Error('Pause timeout'));
                    }, 2000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'pause' });
                });
            },
            resumeQuranAudio: () => {
                console.log('[DEBUG] Resuming Quran audio');
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for resume
                    const handleResume = (event) => {
                        if (event && event.detail && event.detail.isPlaying) {
                            window.removeEventListener('quranAudio-state', handleResume);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-state', handleResume);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-state', handleResume);
                        reject(new Error('Resume timeout'));
                    }, 2000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'resume' });
                });
            },
            stopQuranAudio: () => {
                console.log('[DEBUG] Stopping Quran audio');
                return new Promise((resolve) => {
                    // Stop is always considered successful after a short delay
                    setTimeout(resolve, 500);
                    this.sendToPlatform('quranAudio', { action: 'stop' });
                });
            },
            seekQuranAudio: (index) => {
                console.log('[DEBUG] Seeking Quran audio to index:', index);
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for verse change
                    const handleVerseChange = (event) => {
                        if (event && event.detail && event.detail.verseIndex === index) {
                            window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-verseChanged', handleVerseChange);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                        reject(new Error('Seek timeout'));
                    }, 3000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'seek', index });
                });
            },
            skipToVerse: (index) => {
                // Alias for seekQuranAudio for compatibility
                console.log('[DEBUG] Skipping to verse index:', index);
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for verse change
                    const handleVerseChange = (event) => {
                        if (event && event.detail && (event.detail.verseIndex === index || event.detail.currentVerseIndex === index)) {
                            window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-verseChanged', handleVerseChange);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                        reject(new Error('Skip timeout'));
                    }, 3000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'seek', index });
                });
            },
            getQuranAudioState: () => {
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for state
                    const handleState = (event) => {
                        if (event && event.detail) {
                            window.removeEventListener('quranAudio-state', handleState);
                            resolve(event.detail);
                        }
                    };
                    window.addEventListener('quranAudio-state', handleState);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-state', handleState);
                        reject(new Error('Get state timeout'));
                    }, 2000);

                    // Request state with full context
                    this.sendToPlatform('quranAudio', { 
                        action: 'getState',
                        includeContext: true
                    });
                });
            },
            nextVerse: () => {
                console.log('[DEBUG] Going to next verse');
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for verse change
                    const handleVerseChange = (event) => {
                        if (event && event.detail && (event.detail.verseIndex !== undefined || event.detail.currentVerseIndex !== undefined)) {
                            window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-verseChanged', handleVerseChange);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                        reject(new Error('Next verse timeout'));
                    }, 3000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'next' });
                });
            },
            previousVerse: () => {
                console.log('[DEBUG] Going to previous verse');
                return new Promise((resolve, reject) => {
                    // Set up one-time event listener for verse change
                    const handleVerseChange = (event) => {
                        if (event && event.detail && (event.detail.verseIndex !== undefined || event.detail.currentVerseIndex !== undefined)) {
                            window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                            resolve();
                        }
                    };
                    window.addEventListener('quranAudio-verseChanged', handleVerseChange);

                    // Set timeout for failure
                    setTimeout(() => {
                        window.removeEventListener('quranAudio-verseChanged', handleVerseChange);
                        reject(new Error('Previous verse timeout'));
                    }, 3000);

                    // Send command to native
                    this.sendToPlatform('quranAudio', { action: 'previous' });
                });
            }
        };
        
        // Set up event handler for adhan events from native code
        window.handleAdhanEvent = function(eventData) {
            console.log('[DEBUG] Received adhan event:', eventData);
            
            // Extract event type and data
            const { event, data } = eventData;
            
            // Create and dispatch a custom event
            const customEvent = new CustomEvent('adhan-' + event, {
                detail: data
            });
            
            // Dispatch the event
            window.dispatchEvent(customEvent);
        };

        // Set up event handler for Quran audio events from native code
        window.handleQuranAudioEvent = function(eventData) {
            console.log('[DEBUG] Received Quran audio event:', eventData);
            const { event, data } = eventData;
            const customEvent = new CustomEvent('quranAudio-' + event, {
                detail: data
            });
            window.dispatchEvent(customEvent);
        };
    }
    
    /**
     * Initialize debug utilities for testing
     */
    initializeDebugUtilities() {
        window.debugBridge = {
            getSettings: () => {
                console.log('[DEBUG] Current settings:', this.settings);
                return this.settings;
            },
            getUser: () => {
                console.log('[DEBUG] Current user:', auth.currentUser);
                return auth.currentUser;
            },
            getBridgeState: () => {
                const state = {
                    platform: this.platform,
                    lastDataSentDate: this.lastDataSentDate,
                    hasSettings: !!this.settings,
                    settings: this.settings,
                    user: auth.currentUser,
                    bridgeExists: !!window.ummahtiIosBridge,
                    methods: window.ummahtiIosBridge ? Object.keys(window.ummahtiIosBridge) : [],
                    webkitExists: !!window.webkit,
                    messageHandlersExist: !!window.webkit?.messageHandlers,
                    availableHandlers: window.webkit?.messageHandlers ? Object.keys(window.webkit.messageHandlers) : [],
                    androidExists: !!window.AndroidInterface,
                    androidMethods: this.androidInterface ? Object.getOwnPropertyNames(Object.getPrototypeOf(this.androidInterface)) : []
                };
                console.log('[DEBUG] Current bridge state:', state);
                return state;
            }
        };

        // Log bridge initialization state
        const initState = {
            platform: this.platform,
            exists: !!window.ummahtiIosBridge,
            methods: window.ummahtiIosBridge ? Object.keys(window.ummahtiIosBridge) : [],
            webkitExists: !!window.webkit,
            messageHandlersExist: !!window.webkit?.messageHandlers,
            availableHandlers: window.webkit?.messageHandlers ? Object.keys(window.webkit.messageHandlers) : [],
            androidExists: !!window.AndroidInterface
        };
        console.log('[DEBUG] Bridge initialized:', initState);
    }
    
    /**
     * Send data to the appropriate platform
     * @param {string} type - Type of message (prayerTimes, adhan, etc)
     * @param {object} data - Data to send
     * @returns {boolean} - Success status
     */
    sendToPlatform(type, data) {
        try {
            if (this.platform === 'ios') {
                // iOS platform
                if (type === 'prayerTimes' && window.webkit?.messageHandlers?.prayerTimesHandler) {
                    window.webkit.messageHandlers.prayerTimesHandler.postMessage(data);
                    console.log('[DEBUG] Successfully sent prayerTimes data to iOS');
                    return true;
                } else if (type === 'adhan' && window.webkit?.messageHandlers?.adhanHandler) {
                    window.webkit.messageHandlers.adhanHandler.postMessage(data);
                    console.log('[DEBUG] Successfully sent adhan data to iOS');
                    return true;
                } else if (type === 'quranAudio' && window.webkit?.messageHandlers?.quranAudioHandler) {
                    window.webkit.messageHandlers.quranAudioHandler.postMessage(data);
                    console.log('[DEBUG] Successfully sent Quran audio data to iOS');
                    return true;
                } else {
                    console.error(`[ERROR] iOS message handler not available for type: ${type}`);
                    return false;
                }
            } else if (this.platform === 'android') {
                // Android platform
                if (!this.androidInterface) {
                    console.error('[ERROR] Android interface not available');
                    return false;
                }
                
                if (type === 'prayerTimes') {
                    // Convert to JSON string as Android expects it
                    const jsonData = JSON.stringify(data);
                    this.androidInterface.updatePrayerTimes(jsonData);
                    console.log('[DEBUG] Successfully sent prayerTimes data to Android');
                    return true;
                } else if (type === 'adhan') {
                    // Handle different adhan actions
                    const jsonData = JSON.stringify(data);
                    switch (data.action) {
                        case 'download':
                            if (data.fileName && data.fileURL) {
                                this.androidInterface.downloadAdhan(data.fileName, data.fileURL);
                                console.log('[DEBUG] Called Android downloadAdhan:', data.fileName, data.fileURL);
                            }
                            break;
                        case 'play':
                            this.androidInterface.playAdhan(data.fileName, data.fileURL || "");
                            break;
                        case 'stop':
                            this.androidInterface.stopAdhan();
                            break;
                        case 'schedule':
                            this.androidInterface.scheduleAdhan(data.prayerName, data.time, data.fileName);
                            break;
                        case 'cancelSchedule':
                            this.androidInterface.cancelScheduledAdhan(data.prayerName);
                            break;
                        case 'cancelAllSchedules':
                            this.androidInterface.cancelAllScheduledAdhans();
                            break;
                        case 'updateSettings':
                            this.androidInterface.updateAdhanSettings(
                                data.regularAdhanURL, 
                                data.fajrAdhanURL,
                                data.regularFileName,
                                data.fajrFileName
                            );
                            break;
                        default:
                            // For any other action, pass the whole JSON
                            this.androidInterface.processAdhanAction(jsonData);
                            break;
                    }
                    
                    console.log(`[DEBUG] Successfully sent adhan action ${data.action} to Android`);
                    return true;
                } else if (type === 'quranAudio') {
                    // Handle different Quran audio actions
                    if (!this.androidInterface) {
                        console.error('[ERROR] Android interface not available for Quran audio');
                        return false;
                    }
                    
                    switch (data.action) {
                        case 'play':
                            const audioFilesJson = JSON.stringify(data.audioFiles);
                            this.androidInterface.playQuranAudio(
                                audioFilesJson, 
                                data.startIndex || 0, 
                                data.surahName || "", 
                                data.reciterName || "Quran Recitation"
                            );
                            break;
                        case 'pause':
                            this.androidInterface.pauseQuranAudio();
                            break;
                        case 'resume':
                            this.androidInterface.resumeQuranAudio();
                            break;
                        case 'stop':
                            this.androidInterface.stopQuranAudio();
                            break;
                        case 'seek':
                            this.androidInterface.seekToVerse(data.index || 0);
                            break;
                        case 'next':
                            this.androidInterface.nextVerse();
                            break;
                        case 'previous':
                            this.androidInterface.previousVerse();
                            break;
                        case 'getState':
                            // For getState, we need to return the result
                            try {
                                const stateJson = this.androidInterface.getQuranAudioState();
                                const state = JSON.parse(stateJson);
                                // Dispatch event with state
                                const js = `window.dispatchEvent(new CustomEvent('quranAudio-state', { detail: ${stateJson} }))`;
                                window.androidWebView?.evaluateJavascript(js, null);
                            } catch (e) {
                                console.error('[ERROR] Error getting Quran audio state:', e);
                            }
                            break;
                        default:
                            console.error(`[ERROR] Unknown Quran audio action: ${data.action}`);
                            return false;
                    }
                    
                    console.log(`[DEBUG] Successfully sent Quran audio action ${data.action} to Android`);
                    return true;
                } else {
                    console.error(`[ERROR] Unknown message type for Android: ${type}`);
                    return false;
                }
            } else {
                // Web platform - graceful degradation
                console.log(`[DEBUG] No native implementation available for ${type} on web platform`);
                // For web, we could implement fallbacks here (e.g., browser notifications)
                return false;
            }
        } catch (error) {
            console.error(`[ERROR] Failed to send ${type} to ${this.platform}:`, error);
            return false;
        }
    }
    
    /**
     * Update bridge settings
     * @param {object} settings - New settings
     */
    updateSettings(settings) {
        console.log('[DEBUG] Updating bridge settings:', {
            platform: this.platform,
            oldSettings: this.settings,
            newSettings: settings,
            hasNewSettings: !!settings
        });
        this.settings = settings;
        
        // If settings include adhan settings, update them in the native app
        if (settings?.selectedAdhan?.regularPath && settings?.selectedAdhan?.fajrPath) {
            // Use our getAdhanURLs method for consistency
            const updateAdhanSettings = async () => {
                try {
                    // Get adhan URLs using our helper method
                    const adhanURLs = await window.ummahtiIosBridge.getAdhanURLs();
                    
                    if (adhanURLs) {
                        // Update adhan settings with the resolved URLs
                        window.ummahtiIosBridge.updateAdhanSettings({
                            regularAdhanURL: adhanURLs.regularAdhanURL,
                            fajrAdhanURL: adhanURLs.fajrAdhanURL
                        });
                    }
                } catch (error) {
                    console.error('[ERROR] Error updating adhan settings:', error);
                }
            };
            
            // Execute the async function
            updateAdhanSettings();
        } else {
            console.log('[DEBUG] No adhan settings provided (possibly guest user)');
        }
    }
    
    /**
     * Initialize the bridge
     * @param {object} settings - Settings to initialize with
     * @param {boolean} forceRefresh - Force refresh regardless of last refresh date
     * @returns {Promise<object|null>} - Result of initialization
     */
    async initializeBridge(settings, forceRefresh = false) {
        console.log('[DEBUG] initializeBridge called with settings:', {
            platform: this.platform,
            hasSettings: !!settings,
            settingsKeys: settings ? Object.keys(settings) : [],
            forceRefresh
        });
        
        // Update settings if provided
        if (settings) {
            this.updateSettings(settings);
        }
        
        // Check for saved adhan settings
        const savedAdhanSettings = localStorage.getItem('adhanSettings');
        if (savedAdhanSettings) {
            try {
                const adhanSettings = JSON.parse(savedAdhanSettings);
                // Only use if not too old (7 days)
                const isRecent = (Date.now() - adhanSettings.timestamp) < (7 * 24 * 60 * 60 * 1000);
                
                if (isRecent && adhanSettings.regularAdhanURL && adhanSettings.fajrAdhanURL) {
                    console.log('[DEBUG] Using saved adhan settings:', adhanSettings);
                    
                    // Only update if we don't already have settings with adhan info
                    if (!settings?.selectedAdhan?.regularPath) {
                        window.ummahtiIosBridge.updateAdhanSettings({
                            regularAdhanURL: adhanSettings.regularAdhanURL,
                            fajrAdhanURL: adhanSettings.fajrAdhanURL
                        });
                    }
                }
            } catch (error) {
                console.error('[ERROR] Error parsing saved adhan settings:', error);
            }
        }

        const today = new Date().toDateString();
        const needsRefresh = forceRefresh || this.lastDataSentDate !== today || this.lastLoginState;
        
        if (needsRefresh) {
            console.log('[DEBUG] Getting fresh prayer times data due to:', {
                forceRefresh,
                lastRefreshWasToday: this.lastDataSentDate === today,
                hasLoginStateChange: !!this.lastLoginState,
                loginState: this.lastLoginState
            });
            return window.ummahtiIosBridge.getPrayerTimesAndLocation();
        } else {
            console.log('[DEBUG] Bridge already initialized today');
            return null;
        }
    }

    /**
     * Fetches prayer times for multiple days ahead
     * @param {Object} settings - User settings with location, calculation method, etc.
     * @param {Number} daysAhead - Number of days to fetch (defaults to 6 to get a week total with today)
     * @returns {Promise<Array>} - Array of prayer time dictionaries
     */
    async fetchWeekPrayerTimes(settings, daysAhead = 6) {
        console.log('[DEBUG] Fetching prayer times for the next', daysAhead, 'days');
        
        try {
            const weekPrayerTimes = [];
            
            // Use Promise.all to fetch all days in parallel for better performance
            const promises = [];
            for (let dayOffset = 1; dayOffset <= daysAhead; dayOffset++) {
                promises.push(this.fetchPrayerTimesForDay(settings, dayOffset));
            }
            
            const results = await Promise.all(promises);
            
            // Filter out any failed results and convert to the format expected by iOS
            for (const result of results) {
                if (result && result.times) {
                    weekPrayerTimes.push({
                        fajr: result.times.Fajr,
                        dhuhr: result.times.Dhuhr,
                        asr: result.times.Asr,
                        maghrib: result.times.Maghrib,
                        isha: result.times.Isha
                    });
                }
            }
            
            console.log('[DEBUG] Successfully fetched', weekPrayerTimes.length, 'days of prayer times');
            return weekPrayerTimes;
        } catch (error) {
            console.error('[ERROR] Error fetching week prayer times:', error);
            return [];
        }
    }
    
    /**
     * Fetches prayer times for a specific day offset from today
     * @param {Object} settings - User settings with location, calculation method, etc.
     * @param {Number} dayOffset - Days from today (1 = tomorrow, etc.)
     * @returns {Promise<Object>} - Prayer times for the day
     */
    async fetchPrayerTimesForDay(settings, dayOffset) {
        try {
            // Calculate the target date
            const targetDate = new Date();
            targetDate.setDate(targetDate.getDate() + dayOffset);
            
            // Use the existing prayerService function
            return await fetchPrayerTimes({
                location: settings.location,
                coordinates: settings.coordinates,
                calculationMethod: settings.calculationMethod,
                timezone: settings.timezone,
                tunedTimes: settings.tunedTimes
            }, targetDate);
        } catch (error) {
            console.error(`[ERROR] Failed to fetch prayer times for day offset ${dayOffset}:`, error);
            return null;
        }
    }

    /**
     * Handle authentication state changes
     * @param {string} state - The auth state change ('loggedIn' or 'loggedOut')
     */
    handleAuthStateChange(state) {
        console.log('[DEBUG] Handling auth state change:', state);
        this.lastLoginState = state;
        
        if (window.ummahtiIosBridge && window.ummahtiIosBridge.notifyAuthStateChanged) {
            window.ummahtiIosBridge.notifyAuthStateChanged(state);
        }
        
        // Force a refresh for both login and logout to ensure widgets get updated
        console.log(`[DEBUG] Forcing prayer times refresh due to auth state change: ${state}`);
        // Use current settings but force a refresh
        this.initializeBridge(this.settings, true);
    }
}

// Create instance immediately
const nativeBridge = new NativeBridge();

// Make sure it's globally available for backward compatibility
window.iosBridge = nativeBridge; 

export default nativeBridge; 